import type { Route, Routes } from '@angular/router';
import { lazyProvider } from '@ngxs/store';

import { PagesCanLoadGuard } from '@efficiently/client-core-guards';
import { APP_ROUTES } from '@efficiently/client-core-routing';

import { createAppRoutes } from './app.routes';
import type { DesignSectionRouteOptions } from './design-section-route-options.model';
import { PROJECT_NAVIGATOR_ENABLED_TOKEN } from './pages/projects/navigator/tokens/project-navigator-enabled.token';
import { PlatformShellComponent } from './shell/platform-shell.component';

/**
 * The Design section as this build runs it: in the platform shell's layout (EFF-26168), which only this
 * build has.
 *
 * The flag is what the Design routes and components branch on. The state is the project list's slice:
 * the project create, clone, archive and demo flows dispatch into it outside the Projects list route,
 * which is the only route the other build registers it on — and one this build never matches.
 * `lazyProvider` keeps that slice out of the initial chunk.
 */
const DESIGN_SECTION: DesignSectionRouteOptions = {
	providers: [{ provide: PROJECT_NAVIGATOR_ENABLED_TOKEN, useValue: true }],
	canActivate: [
		lazyProvider(() =>
			import('./pages/projects/components/project-list/project-list-states').then(
				m => m.projectListStateProvider,
			),
		),
	],
};

/** The environment's own path — empty, so it consumes no segment and changes no section's URL. */
const CLARITY_ENVIRONMENT_PATH = '';

const MCP_OAUTH_CONSENT_PATH = 'oauth/consent';

/**
 * Where this build lands the application root: the Control Center, whose own `/clarity` screen opens the
 * person's first working domain. A sign-in and an unknown URL arrive here too, through
 * `APP_ROOT_URL`. Design keeps `/projects` as its own landing, reached from the section switcher.
 */
const ROOT_LANDING_ROUTE: Route = {
	path: APP_ROUTES.PAGES.path,
	pathMatch: 'full',
	redirectTo: `/${APP_ROUTES.CLARITY.path}`,
};

/** Brain's MCP OAuth consent screen, `/oauth/consent?req=<request token>`, outside the shell. */
const MCP_OAUTH_CONSENT_ROUTE: Route = {
	path: MCP_OAUTH_CONSENT_PATH,
	canActivate: [PagesCanLoadGuard],
	loadChildren: () => import('./clarity/clarity-mcp-consent.routes').then(m => m.CLARITY_MCP_CONSENT_APP_ROUTES),
};

/**
 * The route table of the build WITH the Control Center — the `*-clarity` configurations.
 *
 * Reached only from `main.clarity.ts`, which is the whole separation: this file names the platform
 * shell, so the chrome it composes ships here and nowhere else.
 *
 * Everything Clarity lives behind the two `import()`s below and nowhere else, and they divide the
 * stack by who needs it. The **environment** wrapper carries what BOTH sections share — the Clarity
 * transports, the session, the realtime relay, the domain context, the chat streams, the domain chat
 * and the AI Chat panel's content — because the panel opens in Design too (EFF-26319, BR-9). The
 * `clarity` section's own chunk keeps its routes and the five tab-only data-access calls, so that code
 * loads with the section that reads it. Both are loaded rather than imported so that none of the
 * Clarity stack reaches the initial chunk: the providers used to be written at the call site, and the
 * static import they needed put the whole session library in the initial bundle although the route was
 * already lazy.
 *
 * The environment wraps the shell's children rather than sitting beside them, which is what puts it
 * above the Design child as well as above `clarity` — see `createAppRoutes`'s fourth parameter.
 */
export const PLATFORM_APP_ROUTES: Routes = [
	MCP_OAUTH_CONSENT_ROUTE,
	...createAppRoutes(
		PlatformShellComponent,
		[
			ROOT_LANDING_ROUTE,
			{
				path: APP_ROUTES.CLARITY.path,
				loadChildren: () => import('./clarity/clarity.routes').then(m => m.CLARITY_APP_ROUTES),
			},
			{
				path: APP_ROUTES.SUPPLY.path,
				loadChildren: () => import('./supply/supply.routes').then(m => m.SUPPLY_APP_ROUTES),
			},
		],
		DESIGN_SECTION,
		children => [
			{
				path: CLARITY_ENVIRONMENT_PATH,
				loadChildren: () =>
					import('./clarity/clarity-environment.routes').then(m =>
						m.createClarityEnvironmentRoutes(children),
					),
			},
		],
	),
];
